import {
  type DeliveryProposalTableDataType,
  getDraftOfferCargoRows,
} from "@/pages/shipper-cargo-detail/ui/utils";
import { formatMoney } from "@/shared/lib/currency";
import { toLocaleTextAsDateTime } from "@/shared/lib/datetime";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseHeaderWrapper } from "@/shared/ui/base-header-wrapper";
import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import { DeliveryProposalRejectionDialog } from "@/widgets/delivery-proposal-rejection-dialog";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import type React from "react";
import { useMemo, useState } from "react";
import type { ShipperCargoDetailPageProps } from "../model";

export type DeliveryProposalsInfoProps = {
  offerCargoProcess: ShipperCargoDetailPageProps["offerCargoProcess"];
  onReject: ShipperCargoDetailPageProps["onDeliveryProposalReject"];
};

export const DeliveryProposalsInfo: React.FC<DeliveryProposalsInfoProps> = ({
  offerCargoProcess,
  onReject,
}) => {
  const [
    deliveryProposalOfferCargoIdToReject,
    setDeliveryProposalOfferCargoIdToReject,
  ] = useState<number | null>(null);

  const SuggestTable = BaseTable<DeliveryProposalTableDataType>;
  const columns: Column<DeliveryProposalTableDataType>[] = [
    {
      key: "companyName",
      label: "企業名",
      cell: (row) => {
        return <Typography color={grey[900]}>{row.companyName}</Typography>;
      },
    },
    {
      key: "deliveryProposalContractStatus",
      label: "契約状況",
      cell: (row) => {
        return (
          <OfferCargoStatusBadge
            statusType="delivery_proposal_contract"
            status={row.deliveryProposalContractStatus}
          />
        );
      },
    },
    {
      key: "companyName",
      label: "返答期限",
      cell: (row) => {
        return (
          <Typography color={grey[900]}>
            {toLocaleTextAsDateTime(row.responseDeadlineAt)}
          </Typography>
        );
      },
    },
    {
      key: "proposedAt",
      label: "提案日時",
      cell: (row) => {
        return <Typography color={grey[900]}>{row.proposedAt}</Typography>;
      },
    },
    {
      key: "requiredShippingFee",
      label: "運賃",
      cell: (row) => {
        const requiredShippingFee = row.requiredShippingFee;
        const proposedFeeDiff = row.proposedFeeDiff;
        return (
          <Stack spacing={0} alignItems="end">
            <Typography variant="body1">
              {formatMoney(requiredShippingFee)}
            </Typography>
            {proposedFeeDiff !== 0 && proposedFeeDiff !== undefined && (
              <Typography variant="caption">
                {proposedFeeDiff > 0 ? "+" : "-"}
                {formatMoney(Math.abs(proposedFeeDiff))}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      key: "id",
      label: "",
      disabledLink: true,
      cell: (row) => {
        if (row.deliveryProposalContractStatus !== "not_responded") {
          return null;
        }
        const id = row.id;
        return (
          <Stack>
            <BaseButton
              color="primary"
              variant="outlined"
              size="small"
              onClick={() => {
                setDeliveryProposalOfferCargoIdToReject(id);
              }}
              sx={{
                width: 70,
                minWidth: 70,
                bgcolor: "white",
              }}
            >
              見送る
            </BaseButton>
            <DeliveryProposalRejectionDialog
              open={!!deliveryProposalOfferCargoIdToReject}
              onClose={() => setDeliveryProposalOfferCargoIdToReject(null)}
              onSubmit={(rejectParams) => {
                if (!deliveryProposalOfferCargoIdToReject) {
                  return;
                }
                onReject(deliveryProposalOfferCargoIdToReject, rejectParams);
                setDeliveryProposalOfferCargoIdToReject(null);
              }}
            />
          </Stack>
        );
      },
    },
  ];

  const rows = useMemo(
    () => getDraftOfferCargoRows(offerCargoProcess),
    [offerCargoProcess],
  );

  if (offerCargoProcess.status === "loading") {
    return (
      <BaseHeaderWrapper id="delivery-proposal-list" title={"配送提案"}>
        <CircularProgress />
      </BaseHeaderWrapper>
    );
  }
  if (offerCargoProcess.status === "error") {
    return (
      <BaseHeaderWrapper id="delivery-proposal-list" title={"配送提案"}>
        <Typography>{offerCargoProcess.error.message}</Typography>
      </BaseHeaderWrapper>
    );
  }
  const deliveryProposalOfferCargos =
    offerCargoProcess.data.deliveryProposalOfferCargos;
  return (
    <BaseHeaderWrapper
      id="delivery-proposal-list"
      title={`配送提案${deliveryProposalOfferCargos ? ` (${deliveryProposalOfferCargos.length}件)` : ""}`}
    >
      <SuggestTable
        columns={columns}
        rows={rows}
        to="/shipper/delivery-proposals/$id"
        params={(row) => ({
          id: row.id,
        })}
        noDataMessage="現在、提案がございません。"
      />
    </BaseHeaderWrapper>
  );
};
